import { useState, useEffect, useCallback, useRef } from 'react';
import { Project } from '@/mocks/projects';

interface Props {
  project: Project | null;
  onClose: () => void;
  onNextProject?: () => void;
  onPrevProject?: () => void;
}

const AUTOPLAY_MS = 5000;

function isVideo(url: string) {
  return /\.(mp4|mov|webm|ogg)(\?.*)?$/i.test(url);
}

const ProjectStoryViewer = ({ project, onClose, onNextProject, onPrevProject }: Props) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const allMedia = project
    ? [
        ...project.imgs.map((url) => ({ type: 'image' as const, url })),
        ...(project.videos || []).map((url) => ({ type: 'video' as const, url })),
      ]
    : [];

  const totalMedia = allMedia.length;
  const activeMedia = allMedia[activeIndex];
  const isActiveVideo = activeMedia?.type === 'video';

  const goNext = useCallback(() => {
    if (activeIndex < totalMedia - 1) {
      setActiveIndex((i) => i + 1);
    } else if (onNextProject) {
      onNextProject();
    }
  }, [activeIndex, totalMedia, onNextProject]);

  const goPrev = useCallback(() => {
    if (activeIndex > 0) {
      setActiveIndex((i) => i - 1);
    } else if (onPrevProject) {
      onPrevProject();
    }
  }, [activeIndex, onPrevProject]);

  // Reset on project change
  useEffect(() => {
    setActiveIndex(0);
    setProgress(0);
    progressRef.current = 0;
    if (project) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
      if (timerRef.current) clearInterval(timerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [project]);

  // Autoplay timer (skip when video is active and playing)
  useEffect(() => {
    if (!project || isPaused || isActiveVideo) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    lastTimeRef.current = performance.now();
    timerRef.current = setInterval(() => {
      goNext();
    }, AUTOPLAY_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [project, activeIndex, isPaused, goNext, isActiveVideo]);

  // Progress bar animation (skip when video is active)
  useEffect(() => {
    if (!project || isPaused || isActiveVideo) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setProgress(0);
      return;
    }
    const animate = (now: number) => {
      const elapsed = now - lastTimeRef.current;
      const p = Math.min((elapsed / AUTOPLAY_MS) * 100, 100);
      setProgress(p);
      if (p < 100) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };
    lastTimeRef.current = performance.now();
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [project, activeIndex, isPaused, isActiveVideo]);

  // Keyboard
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'Escape') onClose();
    };
    if (project) window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [project, goNext, goPrev, onClose]);

  // Touch swipe
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.changedTouches[0].clientX);
    setIsPaused(true);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const endX = e.changedTouches[0].clientX;
    const diff = (touchStartX ?? 0) - endX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goNext();
      else goPrev();
    }
    setIsPaused(false);
  };

  if (!project) return null;

  const isFirst = activeIndex === 0;

  return (
    <div
      className="fixed inset-0 bg-black z-50 flex flex-col"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-30 flex items-center gap-2 px-3 pt-3 pb-2">
        {/* Progress segments */}
        <div className="flex-1 flex gap-1">
          {allMedia.map((_, i) => (
            <div
              key={i}
              className="flex-1 h-1 rounded-full bg-white/20 overflow-hidden"
            >
              <div
                className="h-full rounded-full bg-white transition-none"
                style={{
                  width:
                    i < activeIndex
                      ? '100%'
                      : i === activeIndex
                      ? `${progress}%`
                      : '0%',
                }}
              />
            </div>
          ))}
        </div>
        <button
          onClick={onClose}
          className="w-9 h-9 flex items-center justify-center bg-black/40 hover:bg-black/60 rounded-full text-white transition-colors cursor-pointer shrink-0 ml-2"
          aria-label="Cerrar"
        >
          <i className="ri-close-line text-lg"></i>
        </button>
      </div>

      {/* Paused indicator */}
      {isPaused && !isActiveVideo && (
        <div className="absolute top-14 left-1/2 -translate-x-1/2 z-30 bg-black/50 backdrop-blur-sm text-white text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1.5">
          <i className="ri-pause-line"></i>
          Pausado
        </div>
      )}

      {/* Main media area — object-contain for full photo visibility */}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden bg-black">
        <div
          className="flex h-full w-full transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {allMedia.map((media, i) => (
            <div
              key={i}
              className="min-w-full h-full flex items-center justify-center p-0 md:p-6"
            >
              {media.type === 'video' ? (
                <video
                  ref={i === activeIndex ? videoRef : undefined}
                  src={media.url}
                  controls
                  preload="metadata"
                  poster={project.imgs[0]}
                  className="max-w-full max-h-full object-contain"
                  draggable={false}
                  onEnded={() => {
                    if (i === activeIndex) goNext();
                  }}
                  onPlay={() => {
                    if (i === activeIndex) {
                      setIsPaused(true);
                      setProgress(0);
                    }
                  }}
                  onPause={() => {
                    if (i === activeIndex) {
                      setIsPaused(false);
                    }
                  }}
                />
              ) : (
                <img
                  src={media.url}
                  alt={`${project.title} — foto ${i + 1}`}
                  className="max-w-full max-h-full object-contain"
                  draggable={false}
                  onError={(e) => {
                    const t = e.currentTarget;
                    t.style.display = 'none';
                  }}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Navigation arrows (hover) */}
      <button
        onClick={goPrev}
        className="absolute left-0 top-12 bottom-20 w-1/4 md:w-24 z-20 flex items-center justify-start pl-2 md:pl-4 opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
        aria-label="Anterior"
      >
        <div className="w-10 h-10 flex items-center justify-center bg-black/40 hover:bg-black/60 rounded-full text-white">
          <i className="ri-arrow-left-line text-lg"></i>
        </div>
      </button>
      <button
        onClick={goNext}
        className="absolute right-0 top-12 bottom-20 w-1/4 md:w-24 z-20 flex items-center justify-end pr-2 md:pr-4 opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
        aria-label="Siguiente"
      >
        <div className="w-10 h-10 flex items-center justify-center bg-black/40 hover:bg-black/60 rounded-full text-white">
          <i className="ri-arrow-right-line text-lg"></i>
        </div>
      </button>

      {/* Counter */}
      <div className="absolute top-14 right-4 z-20 bg-black/40 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-full">
        {activeIndex + 1} / {allMedia.length}
      </div>

      {/* Bottom info bar */}
      <div className="bg-black/80 backdrop-blur-md px-4 py-4 z-30">
        <div className="max-w-4xl mx-auto">
          {/* Info overlay — SOLO en la primera imagen */}
          {isFirst && (
            <div className="mb-3 pb-3 border-b border-white/10">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="bg-[#C88A2A] text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wide">
                  {project.category}
                </span>
                <span className="text-white/60 text-xs flex items-center gap-1">
                  <i className="ri-map-pin-line"></i>
                  {project.location}
                </span>
                <span className="text-white/60 text-xs flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {project.year}
                </span>
                <span className="text-white/60 text-xs flex items-center gap-1">
                  <i className="ri-ruler-line"></i>
                  {project.area}
                </span>
              </div>
              <h2 className="text-lg md:text-xl font-bold text-white leading-tight">
                {project.title}
              </h2>
              <p className="text-white/60 text-sm mt-1 line-clamp-2">
                {project.description}
              </p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {project.tags.slice(0, 4).map((tag) => (
                  <span
                    key={tag}
                    className="bg-white/10 text-white/80 text-[10px] font-semibold px-2 py-0.5 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Thumbnails + nav */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 flex-1 scrollbar-hide">
              {allMedia.map((media, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className={`shrink-0 rounded-md overflow-hidden border-2 transition-all cursor-pointer relative ${
                    i === activeIndex
                      ? 'border-white w-12 h-12'
                      : 'border-transparent w-10 h-10 opacity-50 hover:opacity-80'
                  }`}
                  aria-label={`Media ${i + 1}`}
                >
                  {media.type === 'video' ? (
                    <div className="w-full h-full bg-[#0C1D2E] flex items-center justify-center">
                      <i className="ri-video-line text-white/70 text-xs"></i>
                    </div>
                  ) : (
                    <img
                      src={media.url}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  )}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {onPrevProject && (
                <button
                  onClick={onPrevProject}
                  className="w-8 h-8 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer"
                  aria-label="Proyecto anterior"
                >
                  <i className="ri-arrow-left-s-line text-sm"></i>
                </button>
              )}
              <a
                href={`https://wa.me/56992265118?text=Hola%2C%20me%20interesa%20un%20proyecto%20similar%20a%20${encodeURIComponent(project.title)}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1da851] text-white text-sm font-bold px-4 py-2 rounded-md transition-colors duration-200 whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-base"></i>
                Quiero algo similar
              </a>
              {onNextProject && (
                <button
                  onClick={onNextProject}
                  className="w-8 h-8 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer"
                  aria-label="Proyecto siguiente"
                >
                  <i className="ri-arrow-right-s-line text-sm"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectStoryViewer;